import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { api, useApi } from "../api.ts";
import { useAuth } from "../auth.tsx";
import { Button, ErrorText, LoadError, Loading, useToast } from "../ui/index.tsx";
import { DetectionBadge, GameArt, startCheckout, useStripeEnabled } from "./_shared.tsx";
import { PublicHeading } from "./Games.tsx";
import type { Game } from "../../shared/types.ts";

/** "Lifetime" or the longest duration is highlighted as best value. */
const bestIndex = (g: Game) => {
  const life = g.pricing_tiers.findIndex((t) => /lifetime/i.test(t.dur));
  return life >= 0 ? life : g.pricing_tiers.length - 1;
};

export default function Pricing() {
  const games = useApi<Game[]>("/games");
  const { me } = useAuth();
  const stripe = useStripeEnabled();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const priced = (games.data ?? []).filter((g) => g.pricing_tiers.length > 0);

  useEffect(() => {
    if (params.get("checkout") === "cancelled") { toast("Checkout cancelled. You weren't charged.", "info"); setParams({}, { replace: true }); }
  }, []);

  useEffect(() => {
    if (!location.hash || !games.data) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash, games.data]);

  const buy = async (g: Game, i: number) => {
    if (!me) { navigate(`/register?next=${encodeURIComponent("/pricing#" + g.id)}`); return; }
    setBusy(`${g.id}:${i}`);
    setError(null);
    try {
      if (stripe) await startCheckout(g.id, i);
      else {
        const r = await api.post<{ ticket_id: number }>("/purchase-requests", { game_id: g.id, tier_index: i });
        toast("Purchase request sent", "success");
        navigate(`/support/${r.ticket_id}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  };

  return (
    <>
      <PublicHeading title="Pricing" body={stripe
        ? "Pick a game and a duration. Pay by card and access lands on your account the moment the payment clears."
        : "Pick a game and a duration, from one day to lifetime. Request a purchase and we'll activate it on your account."} />
      <section className="mx-auto max-w-6xl px-6 py-12">
        <ErrorText>{error}</ErrorText>
        {games.loading && !games.data ? <Loading /> : games.error && !games.data ? <LoadError error={games.error} retry={games.reload} /> : priced.length === 0 ? (
          <p className="rounded-[var(--radius-card)] border border-dashed border-line py-12 text-center text-sm text-muted">Pricing is coming soon.</p>
        ) : (
          <div className="mt-2 space-y-6">
            {priced.map((g) => {
              const best = bestIndex(g);
              return (
                <div key={g.id} id={g.id} className="pane hud scroll-mt-28 overflow-hidden md:flex">
                  <div className="relative md:w-72 md:shrink-0">
                    <GameArt game={g} className="aspect-[460/215] md:aspect-auto md:h-full" />
                    <div className="absolute inset-0 bg-gradient-to-t from-surface/90 to-transparent md:bg-gradient-to-r md:from-transparent md:to-surface" />
                  </div>
                  <div className="flex-1 p-5">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                      <h2 className="text-lg font-semibold tracking-tight">{g.name}</h2>
                      <div className="flex items-center gap-3">
                        <Link to={`/games/${g.id}`} className="text-sm text-muted hover:text-fg">Details</Link>
                        <DetectionBadge status={g.detection_status} />
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      {g.pricing_tiers.map((t, i) => (
                        <div key={`${t.dur}-${i}`} className={`relative flex flex-col rounded-lg border p-4 transition-colors ${i === best ? "glow-border border-accent/50 bg-accent-soft" : "border-line bg-bg/70 hover:border-line-strong"}`}>
                          {i === best && <span className="absolute -top-2 right-3 rounded bg-accent px-1.5 text-[10px] font-semibold uppercase leading-4 text-accent-fg">Best value</span>}
                          <span className="kicker">{t.dur}</span>
                          <span className="mt-1 font-display text-3xl font-semibold tabular-nums tracking-tight">{t.price}</span>
                          <Button size="sm" variant={i === best ? "primary" : "secondary"} className="mt-4" loading={busy === `${g.id}:${i}`} disabled={busy !== null} onClick={() => buy(g, i)}>
                            {!me ? "Get started" : stripe ? "Buy now" : "Request"}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-8 text-sm text-faint">
          {stripe ? "Payments are processed by Stripe. We never see your card details." : "Requests open a support ticket; an admin confirms payment and activates your license."}
          {" "}Durations stack: buying more time adds to whatever you have left.
        </p>
      </section>
    </>
  );
}
